import React from 'react'
import { Bell, CheckCircle2, CircleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCommunicationNotifications } from '@/features/communications/notifications/notification-context'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { cn } from '@/shared/lib/utils'

type NotificationVariant = 'success' | 'error'

type NotificationRecord = {
  createdAt: number
  description?: string
  id: string
  read: boolean
  title: string
  variant: NotificationVariant
}

type NotifyInput = {
  description?: string
  title: string
  variant: NotificationVariant
}

type NotificationCenterValue = {
  history: Array<NotificationRecord>
  markAllRead: () => void
  notify: (input: NotifyInput) => void
  unreadCount: number
}

const NotificationCenterContext =
  React.createContext<NotificationCenterValue | null>(null)

function getHistoryId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function getVariantIcon(variant: NotificationVariant) {
  return variant === 'success' ? CheckCircle2 : CircleAlert
}

function getVariantClassName(variant: NotificationVariant) {
  return variant === 'success' ? 'text-primary' : 'text-destructive'
}

function formatNotificationTime(createdAt: number) {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(createdAt)
}

export function NotificationCenterProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [history, setHistory] = React.useState<Array<NotificationRecord>>([])

  const notify = React.useCallback(
    ({ description, title, variant }: NotifyInput) => {
      const record: NotificationRecord = {
        createdAt: Date.now(),
        description,
        id: getHistoryId(),
        read: false,
        title,
        variant,
      }

      setHistory((current) => [record, ...current])

      if (variant === 'success') {
        toast.success(title, { description })

        return
      }

      toast.error(title, { description })
    },
    [],
  )

  const markAllRead = React.useCallback(() => {
    setHistory((current) => {
      if (!current.some((notification) => !notification.read)) {
        return current
      }

      return current.map((notification) =>
        notification.read
          ? notification
          : {
              ...notification,
              read: true,
            },
      )
    })
  }, [])

  const value = React.useMemo(
    () => ({
      history,
      markAllRead,
      notify,
      unreadCount: history.filter((notification) => !notification.read).length,
    }),
    [history, markAllRead, notify],
  )

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  )
}

export function useNotificationCenter() {
  const context = React.useContext(NotificationCenterContext)

  if (!context) {
    throw new Error(
      'useNotificationCenter must be used within NotificationCenterProvider',
    )
  }

  return context
}

function NotificationHistory({
  history,
  hideEmpty = false,
}: {
  history: Array<NotificationRecord>
  hideEmpty?: boolean
}) {
  if (history.length === 0) {
    if (hideEmpty) return null

    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        No notifications yet.
      </div>
    )
  }

  return (
    <div className="max-h-96 space-y-2 overflow-y-auto p-2">
      {history.map((notification) => {
        const Icon = getVariantIcon(notification.variant)

        return (
          <article
            key={notification.id}
            className={cn(
              'rounded-lg border p-3',
              notification.read ? 'bg-card' : 'bg-accent/40',
            )}
          >
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted',
                  getVariantClassName(notification.variant),
                )}
              >
                <Icon className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{notification.title}</p>
                  <time className="shrink-0 text-xs text-muted-foreground">
                    {formatNotificationTime(notification.createdAt)}
                  </time>
                </div>
                {notification.description ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {notification.description}
                  </p>
                ) : null}
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
}

export function NotificationsDropdown() {
  const { t } = useTranslation()
  const {
    history,
    markAllRead,
    unreadCount: sessionUnreadCount,
  } = useNotificationCenter()
  const persistent = useCommunicationNotifications()
  const unreadCount =
    sessionUnreadCount +
    persistent.entries.filter(
      (entry) => !entry.acknowledgedAt && !entry.resolvedAt,
    ).length
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open && unreadCount > 0) {
        markAllRead()
        void persistent
          .acknowledge()
          .catch(() => toast.error(t('notifications:deviceError')))
      }
    },
    [markAllRead, unreadCount, persistent, t],
  )

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? t('notifications:unreadAria', { count: unreadCount })
              : t('notifications:title')
          }
          className="relative"
        >
          <Bell className="size-4" />
          {unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-w-[90vw] p-0">
        <DropdownMenuLabel className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span>{t('notifications:title')}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {t('notifications:sessionAndMessages')}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <PersistentNotificationHistory />
        <NotificationHistory
          history={history}
          hideEmpty={persistent.entries.length > 0}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PersistentNotificationHistory() {
  const { entries } = useCommunicationNotifications()
  const groups = new Map<string, typeof entries>()
  for (const entry of entries)
    groups.set(entry.workspaceId, [
      ...(groups.get(entry.workspaceId) ?? []),
      entry,
    ])

  return (
    <div className="max-h-80 overflow-y-auto p-2">
      {[...groups].map(([workspaceId, notifications]) => (
        <section key={workspaceId}>
          <h3 className="px-2 py-1 text-xs font-medium text-muted-foreground">
            {notifications[0].workspaceName}
          </h3>
          {notifications.map((entry) => (
            <a
              key={entry.id}
              className={cn(
                'mb-2 block rounded-lg border p-3 text-sm',
                !entry.acknowledgedAt && !entry.resolvedAt && 'bg-accent/40',
              )}
              href={`/communications/contacts?workspaceId=${encodeURIComponent(entry.workspaceId)}&conversationId=${encodeURIComponent(entry.conversationId)}`}
            >
              <p className="font-medium">{entry.sender}</p>
              <p>{entry.excerpt}</p>
            </a>
          ))}
        </section>
      ))}
    </div>
  )
}
